import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { v4 as uuidv4 } from 'uuid';
import { testTemplateAPI } from '../../api/testTemplateApi';
import { testDataAPI } from '../../api/testDataApi';
import { listCampuses } from '../../api/campusPortalApi';
import { withoutTestDemoCampus } from '../../utils/testDemoCampus';
import SearchableSelect from '../../components/Portal/SearchableSelect';
import ChipSelect from '../../components/Portal/ChipSelect';
import {
  WEEKDAYS,
  getWeekdayTag,
  setWeekdayTag,
  stripWeekdayTag,
  getTemplateTerm,
  getTemplateYearLevel,
  readResponseGroup,
  findCampusForGroup,
  groupSpellingForCampus,
} from './responseFieldHelpers';
import * as S from './TestResponse.styles';

// 模板筛选（Term / Year）记在本机，老师一般一整天都在录同一个年级
const TEMPLATE_FILTER_STORAGE_KEY = 'portal.testResponse.templateFilter';
const OTHER_GROUP = '__other__';

const readStoredTemplateFilter = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(TEMPLATE_FILTER_STORAGE_KEY) || 'null');
    return { term: stored?.term ?? 'all', year: stored?.year ?? 'all' };
  } catch {
    return { term: 'all', year: 'all' };
  }
};

const matchesFacet = (facetValue, filterValue) => {
  if (filterValue === 'all') return true;
  if (filterValue === 'other') return !facetValue;
  return String(facetValue) === String(filterValue);
};

// 按钮上只写数字，行首已经有 "Term" / "Year" 标签 —— iPad 上一行放得下
const buildFacetChipOptions = (values, labelPrefix) => {
  const present = [...new Set(values.filter(Boolean))].sort((a, b) => a - b);
  const chips = [
    { value: 'all', label: 'All' },
    ...present.map(v => ({ value: v, label: String(v), title: `${labelPrefix} ${v}` })),
  ];
  if (values.some(v => !v)) chips.push({ value: 'other', label: 'Other' });
  return chips;
};

const TestResponse = ({ backPath, responsePath }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const dashboardPath = backPath || '/dashboard';
  const testResponsePath = responsePath || '/test-response';
  const [testDataId, setTestDataId] = useState(searchParams.get('id') || uuidv4());
  const [isLoading, setIsLoading] = useState(false);
  const [isExistingResponse, setIsExistingResponse] = useState(false);
  
  // Template 相关状态
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [templateData, setTemplateData] = useState(null);
    // Response 数据
  const [studentName, setStudentName] = useState('');
  const [testResponse, setTestResponse] = useState({});
  const [group, setGroup] = useState('');
  // Group 实际就是校区：按钮直接点选，列表外的（All Students 等）走 Other
  const [campuses, setCampuses] = useState([]);
  const [isOtherGroup, setIsOtherGroup] = useState(false);
  const [templateFilter, setTemplateFilter] = useState(readStoredTemplateFilter);

  // 考试批次：模板定义了batches时必须选择response归属的批次
  const [availableBatches, setAvailableBatches] = useState([]);
  const [selectedBatchId, setSelectedBatchId] = useState('');
  const [templateCurrentBatchId, setTemplateCurrentBatchId] = useState('');

  // Add navigation state
  const [showNavigation, setShowNavigation] = useState(true);

  // Add response navigation states
  const [availableResponses, setAvailableResponses] = useState([]);
  const [currentResponseIndex, setCurrentResponseIndex] = useState(-1);
  const [isNavigationMode, setIsNavigationMode] = useState(false);

  // Add section collapse state
  const [collapsedSections, setCollapsedSections] = useState({});

  // Add exclusion state tracking
  const [excludedSections, setExcludedSections] = useState({});
  const [excludedQuestions, setExcludedQuestions] = useState({});

  // 权限检查
  const userGroups = auth.user?.profile['cognito:groups'] || [];
  const isAdmin = userGroups.includes('site_admin') || userGroups.includes('portal_developer') || userGroups.includes('portal_system_admin') || userGroups.includes('portal_director') || userGroups.includes('portal_manager') || userGroups.includes('portal_admin');
  const isTeacher = userGroups.includes('teacher') || userGroups.includes('portal_teacher');
  const hasPermission = isAdmin || isTeacher;

  // Navigation functions - defined early to avoid hoisting issues
  const loadAvailableResponses = useCallback(async (templateId, currentDataId = null) => {
    if (!templateId || !auth.user?.id_token) return;
    
    try {
      const responses = await testDataAPI.getTestDataByTemplateId(templateId, auth.user.id_token);
      if (Array.isArray(responses)) {
        // Sort responses by student name for consistent ordering
        const sortedResponses = responses.sort((a, b) => a.studentName.localeCompare(b.studentName));
        setAvailableResponses(sortedResponses);
        
        // Use provided currentDataId or fall back to testDataId
        const dataIdToUse = currentDataId || testDataId;
        
        // Find current response index and enable navigation mode if there are responses
        if (dataIdToUse && responses.length > 0) {
          const currentIndex = sortedResponses.findIndex(r => r.id === dataIdToUse);
          setCurrentResponseIndex(currentIndex >= 0 ? currentIndex : -1);
          // Enable navigation mode if there are multiple responses OR if we're viewing an existing response
          setIsNavigationMode(responses.length > 1 || currentIndex >= 0);
        } else if (responses.length > 1) {
          // Enable navigation mode if there are multiple responses available for this template
          setIsNavigationMode(true);
        }
      }
    } catch (error) {
      console.error('Error loading available responses:', error);
    }
  }, [auth.user?.id_token, testDataId]);


  const navigateToResponse = useCallback(async (direction) => {
    if (availableResponses.length === 0) return;
    
    let newIndex;
    if (direction === 'next') {
      newIndex = currentResponseIndex + 1;
      if (newIndex >= availableResponses.length) newIndex = 0; // Loop to first
    } else {
      newIndex = currentResponseIndex - 1;
      if (newIndex < 0) newIndex = availableResponses.length - 1; // Loop to last
    }
    
    const targetResponse = availableResponses[newIndex];
    if (targetResponse) {
      // Preserve templateType parameter in URL and use React Router navigate
      const templateType = searchParams.get('templateType');
      const newPath = templateType
        ? `${testResponsePath}?id=${targetResponse.id}&templateType=${templateType}`
        : `${testResponsePath}?id=${targetResponse.id}`;
      navigate(newPath, { replace: true });
    }
  }, [availableResponses, currentResponseIndex, navigate, searchParams, testResponsePath]);


  // 页面初始化时滚动到顶部
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Keyboard navigation shortcuts
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Only trigger navigation if in navigation mode and not typing in an input
      if (!isNavigationMode || 
          availableResponses.length <= 1 || 
          isLoading ||
          event.target.tagName === 'INPUT' || 
          event.target.tagName === 'TEXTAREA' || 
          event.target.tagName === 'SELECT') {
        return;
      }

      if (event.ctrlKey || event.metaKey) {
        switch (event.key) {
          case 'ArrowLeft':
            event.preventDefault();
            navigateToResponse('prev');
            break;
          case 'ArrowRight':
            event.preventDefault();
            navigateToResponse('next');
            break;
          default:
            break;
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isNavigationMode, availableResponses.length, isLoading, navigateToResponse]);

  // 加载可用的模板（根据templateType URL参数过滤）
  useEffect(() => {
    const loadTemplates = async () => {
      if (!auth.isAuthenticated || !hasPermission) return;
      
      try {
        const templates = await testTemplateAPI.getAllTemplates(auth.user?.id_token);
        
        // 获取URL中的templateType参数
        const urlTemplateType = searchParams.get('templateType');
        
        let filteredTemplates;
        if (urlTemplateType) {
          // 如果有templateType参数，只显示对应类型的模板
          filteredTemplates = templates.filter(template => {
            const templateType = template.templateData?.templateType || 'mockTest';
            return templateType === urlTemplateType;
          });
        } else {
          // 如果没有templateType参数，显示所有模板（保持原有行为）
          filteredTemplates = templates;
        }
        
        setAvailableTemplates(filteredTemplates);
      } catch (error) {
        console.error('Error loading templates:', error);
      }
    };

  loadTemplates();
  }, [auth.isAuthenticated, auth.user?.id_token, hasPermission, searchParams]);

  // 校区列表（Group 按钮）。以前是每选一次模板就把整张 TestData 表拉下来提取组名
  useEffect(() => {
    if (!auth.isAuthenticated || !hasPermission) return undefined;
    let cancelled = false;
    listCampuses()
      .then(list => { if (!cancelled) setCampuses(withoutTestDemoCampus(list)); })
      .catch(error => console.error('Error loading campuses:', error));
    return () => { cancelled = true; };
  }, [auth.isAuthenticated, hasPermission]);

  useEffect(() => {
    try {
      localStorage.setItem(TEMPLATE_FILTER_STORAGE_KEY, JSON.stringify(templateFilter));
    } catch {
      // 无痕模式等情况下存不了，只是下次不记得筛选
    }
  }, [templateFilter]);

  // 模板筛选：Term 优先取字段，Year 只能从模板名里认
  const templateFacets = useMemo(() => availableTemplates.map(template => ({
    template,
    term: getTemplateTerm(template),
    year: getTemplateYearLevel(template),
  })), [availableTemplates]);

  const termChipOptions = useMemo(() => buildFacetChipOptions(templateFacets.map(f => f.term), 'Term'), [templateFacets]);
  const yearChipOptions = useMemo(() => buildFacetChipOptions(templateFacets.map(f => f.year), 'Year'), [templateFacets]);

  // 记住的筛选值在当前模板里已经不存在时（比如换了 templateType），当作 All
  const effectiveTermFilter = termChipOptions.some(o => String(o.value) === String(templateFilter.term)) ? templateFilter.term : 'all';
  const effectiveYearFilter = yearChipOptions.some(o => String(o.value) === String(templateFilter.year)) ? templateFilter.year : 'all';

  const { templateOptions, matchingTemplateCount } = useMemo(() => {
    const matching = templateFacets.filter(({ term, year }) => (
      matchesFacet(term, effectiveTermFilter) && matchesFacet(year, effectiveYearFilter)
    ));
    // 已选中的模板即使被筛掉也留在列表里，否则切换筛选会让选择框变空，看起来像没选
    const selectedFacet = templateFacets.find(f => f.template.id === selectedTemplateId);
    const shown = selectedFacet && !matching.includes(selectedFacet) ? [...matching, selectedFacet] : matching;
    const options = [...shown]
      .sort((a, b) => (
        (a.term || 99) - (b.term || 99)
        || (a.year || 99) - (b.year || 99)
        || String(a.template.templateName).localeCompare(String(b.template.templateName))
      ))
      .map(({ template, term }) => ({
        value: template.id,
        label: template.templateName,
        group: term ? `Term ${term}` : 'Other',
      }));
    return { templateOptions: options, matchingTemplateCount: matching.length };
  }, [templateFacets, effectiveTermFilter, effectiveYearFilter, selectedTemplateId]);

  // 本模板里出现过、但对不上任何校区的 Group（All Students 之类），给 Other 用
  const otherGroupOptions = useMemo(() => {
    const names = new Set();
    availableResponses.forEach((response) => {
      const name = readResponseGroup(response);
      if (name && !findCampusForGroup(name, campuses)) names.add(name);
    });
    return [...names].sort((a, b) => a.localeCompare(b)).map(name => ({ value: name, label: name }));
  }, [availableResponses, campuses]);

  // 加载现有的 test response
  useEffect(() => {
    const loadTestResponse = async () => {
      const urlTestDataId = searchParams.get('id');
      if (urlTestDataId && auth.isAuthenticated) {
        setIsLoading(true);        try {
          const testData = await testDataAPI.getTestData(urlTestDataId, auth.user?.id_token);
          setStudentName(testData.studentName);
          setTestResponse(testData.testResponse);
          setSelectedTemplateId(testData.testTemplateId);
          // 从testResponse中提取group信息
          setGroup(testData.responseGroup || testData.testResponse?.group || testData.group || '');
          setIsOtherGroup(false);
          setIsExistingResponse(true);
          setTestDataId(urlTestDataId);

          // 加载对应的模板
          const template = await testTemplateAPI.getTemplate(testData.testTemplateId, auth.user?.id_token);
          setTemplateData(template.templateData);

          // 加载模板批次；现有response优先用自己已归属的批次
          setAvailableBatches(template.batches || []);
          setTemplateCurrentBatchId(template.currentBatchId || '');
          setSelectedBatchId(testData.batchId || '');
          
          // 加载排除状态到UI state
          loadExclusionStatesFromResponse(testData.testResponse, template.templateData);

          // 加载该模板的所有响应以启用导航（也用来决定校区按钮写入的 Group 写法）
          await loadAvailableResponses(testData.testTemplateId, urlTestDataId);
        } catch (error) {
          console.error('Error loading test response:', error);
          alert('Error loading test response. Starting with new response.');
        } finally {
          setIsLoading(false);
        }
      }
    };    loadTestResponse();
  }, [auth.isAuthenticated, auth.user?.id_token, searchParams, loadAvailableResponses]);
  // 当选择模板时，加载模板数据并初始化响应
  const handleTemplateSelection = async (templateId) => {
    if (!templateId) {
      setTemplateData(null);
      setTestResponse({});
      setGroup(''); // 重置group
      setIsOtherGroup(false);
      setAvailableBatches([]);
      setSelectedBatchId('');
      setTemplateCurrentBatchId('');
      return;
    }

    setIsLoading(true);
    try {
      const template = await testTemplateAPI.getTemplate(templateId, auth.user?.id_token);
      setTemplateData(template.templateData);
      setSelectedTemplateId(templateId);

      // 加载模板批次；新建response默认归属当前批次
      setAvailableBatches(template.batches || []);
      setTemplateCurrentBatchId(template.currentBatchId || '');
      if (!isExistingResponse) {
        setSelectedBatchId(template.currentBatchId || '');
      }

      // 重置group相关状态（只有在不是编辑现有response时才重置）
      if (!isExistingResponse) {
        setGroup('');
        setIsOtherGroup(false);
      }

      // 加载该模板的所有响应以启用导航（也用来决定校区按钮写入的 Group 写法）
      await loadAvailableResponses(templateId);
      
      // 判断模板类型
      const templateType = template.templateData.templateType || 'mockTest';
      
      if (templateType === 'finalExam') {
        // Final Exam 类型：使用新的响应结构
        const initialResponse = {
          responses: []
        };
        setTestResponse(initialResponse);      } else {        // Mock Test 类型：按照questionType来初始化响应结构
        const initialResponse = {};
        Object.keys(template.templateData.sections).forEach(sectionType => {
          const section = template.templateData.sections[sectionType];
          initialResponse[sectionType] = {
            sectionComment: '', // 初始化section comment
            excludedQuestions: [], // 初始化排除的题目列表
            sectionExcluded: false // 初始化整个section的排除状态
          };
          
          // 为主观题初始化评分结构
          const subjectiveQuestions = section.questions.filter(q => q.questionType === 'Subjective');
          subjectiveQuestions.forEach(question => {
            initialResponse[sectionType][`question_${question.questionNumber}`] = {
              score: 0,
              feedback: '',
              excluded: false // 添加exclusion字段
            };
          });
          
          // 为客观题初始化错题列表
          const objectiveQuestions = section.questions.filter(q => q.questionType === 'Objective' || !q.questionType);          if (objectiveQuestions.length > 0) {
            initialResponse[sectionType].incorrectQuestions = [];
          }
        });
        setTestResponse(initialResponse);
      }
    } catch (error) {
      console.error('Error loading template:', error);
      alert('Error loading template. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // 权限和认证检查 - Wait for auth to initialize before redirecting
  if (auth.isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column',
        gap: '16px'
      }}>
        <div style={{
          width: '32px',
          height: '32px',
          border: '3px solid #f3f3f3',
          borderTop: '3px solid #007bff',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <p>Loading...</p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (!auth.isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!hasPermission) {
    return (
      <S.Container>
        <S.Header>
          <S.Title>Access Denied</S.Title>
        </S.Header>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <h2>You don't have permission to access this page</h2>
          <p>Only site administrators and teachers can create or edit test responses.</p>
          <S.Button onClick={() => navigate(dashboardPath)}>Back to Dashboard</S.Button>
        </div>
      </S.Container>
    );
  }

  // Check if current response is in mock test format
  const isCurrentResponseMockFormat = () => {
    return testResponse && !testResponse.responses && typeof testResponse === 'object';
  };

  // Get incorrect questions for mock test format
  const getMockTestIncorrectQuestions = (sectionType) => {
    if (isCurrentResponseMockFormat()) {
      return testResponse[sectionType]?.incorrectQuestions || [];
    }
    return [];
  };

  // Get subjective question data for mock test format
  const getMockTestSubjectiveData = (sectionType, questionNumber) => {
    if (isCurrentResponseMockFormat()) {
      return testResponse[sectionType]?.[`question_${questionNumber}`] || { score: 0, feedback: '' };
    }
    return { score: 0, feedback: '' };
  };

  // 切换错题状态
  const toggleIncorrectQuestion = (sectionType, questionNumber) => {
    if (isCurrentResponseMockFormat()) {
      // Handle mock test format
      setTestResponse(prev => {
        const section = { ...prev[sectionType] };
        const incorrectQuestions = [...(section.incorrectQuestions || [])];
        
        const index = incorrectQuestions.indexOf(questionNumber);
        if (index > -1) {
          incorrectQuestions.splice(index, 1);
        } else {
          incorrectQuestions.push(questionNumber);
        }
        
        return {
          ...prev,
          [sectionType]: {
            ...section,
            incorrectQuestions
          }
        };
      });
    } else {
      // Handle final exam format
      toggleFinalExamIncorrectQuestion(sectionType, questionNumber);
    }
  };  // 更新任意section的Writing类型题目评分（通用函数）
  const updateSectionWritingScore = (sectionType, questionNumber, field, value) => {
    if (isCurrentResponseMockFormat()) {
      // Handle mock test format
      setTestResponse(prev => ({
        ...prev,
        [sectionType]: {
          ...prev[sectionType],
          [`question_${questionNumber}`]: {
            ...prev[sectionType]?.[`question_${questionNumber}`],
            [field]: value
          }
        }
      }));
    } else {
      // Handle final exam format
      updateFinalExamResponse(sectionType, questionNumber, field === 'feedback' ? 'comment' : field, value);
    }
  };

  // 更新section comment（通用函数）
  const updateSectionComment = (sectionType, comment) => {
    if (isCurrentResponseMockFormat()) {
      // Mock Test format: 在section对象中添加comment字段
      setTestResponse(prev => ({
        ...prev,
        [sectionType]: {
          ...prev[sectionType],
          sectionComment: comment
        }
      }));
    } else {
      // Final Exam format: 在responses数组中添加或更新section-level comment
      setTestResponse(prev => {
        const responses = [...(prev.responses || [])];
        const sectionCommentIndex = responses.findIndex(
          r => r.section === sectionType && r.questionNumber === 'section_comment'
        );
        
        if (sectionCommentIndex > -1) {
          responses[sectionCommentIndex] = {
            ...responses[sectionCommentIndex],
            comment: comment
          };
        } else {
          responses.push({
            section: sectionType,
            questionNumber: 'section_comment',
            response: null,
            isCorrect: true,
            score: 0,
            comment: comment
          });
        }
        
        return {
          ...prev,
          responses
        };
      });
    }
  };

  // 获取section comment（通用函数）
  const getSectionComment = (sectionType) => {
    if (isCurrentResponseMockFormat()) {
      return testResponse[sectionType]?.sectionComment || '';
    } else {
      const responses = testResponse.responses || [];
      const sectionCommentResponse = responses.find(
        r => r.section === sectionType && r.questionNumber === 'section_comment'
      );
      return sectionCommentResponse?.comment || '';
    }
  };
  // Final Exam 响应处理函数
  const updateFinalExamResponse = (section, questionNumber, field, value) => {
    setTestResponse(prev => {
      const responses = [...(prev.responses || [])];
      const existingIndex = responses.findIndex(
        r => r.section === section && r.questionNumber === questionNumber
      );
      
      if (existingIndex > -1) {
        responses[existingIndex] = {
          ...responses[existingIndex],
          [field]: value
        };
        
        // 如果是分数字段，自动设置 isCorrect 状态
        if (field === 'score') {
          const question = templateData?.sections[section]?.questions.find(q => q.questionNumber === questionNumber);
          const maxScore = question?.score || 1;
          responses[existingIndex].isCorrect = value >= maxScore;
        }
      } else {
        const newResponse = {
          section,
          questionNumber,
          response: null,
          isCorrect: false,
          score: 0,
          comment: '',
          [field]: value
        };
        
        // 如果是分数字段，自动设置 isCorrect 状态
        if (field === 'score') {
          const question = templateData?.sections[section]?.questions.find(q => q.questionNumber === questionNumber);
          const maxScore = question?.score || 1;
          newResponse.isCorrect = value >= maxScore;
        }
        
        responses.push(newResponse);
      }
      
      return {
        ...prev,
        responses
      };
    });
  };

  // Final Exam 切换错题状态（Math和Reading部分使用）
  const toggleFinalExamIncorrectQuestion = (sectionType, questionNumber) => {
    setTestResponse(prev => {
      const responses = [...(prev.responses || [])];
      const existingIndex = responses.findIndex(
        r => r.section === sectionType && r.questionNumber === questionNumber
      );
      
      if (existingIndex > -1) {
        responses[existingIndex] = {
          ...responses[existingIndex],
          isCorrect: !responses[existingIndex].isCorrect,
          score: responses[existingIndex].isCorrect ? 0 : 1 // 简单的评分逻辑：正确1分，错误0分
        };
      } else {
        responses.push({
          section: sectionType,
          questionNumber,
          response: null,
          isCorrect: false,
          score: 0,
          comment: ''
        });
      }
      
      return {
        ...prev,
        responses
      };
    });
  };

  const getFinalExamResponse = (section, questionNumber) => {
    const responses = testResponse.responses || [];
    return responses.find(r => r.section === section && r.questionNumber === questionNumber) || {
      response: null,
      isCorrect: true, // 默认为正确
      score: 0,
      comment: ''
    };
  };
  // 保存测试响应
  const saveTestResponse = async () => {
    if (!studentName.trim()) {
      alert('Please enter student name.');
      return;
    }
    
    if (!selectedTemplateId) {
      alert('Please select a test template.');
      return;
    }

    // 模板定义了批次时，response必须归属某个批次
    if (availableBatches.length > 0 && !selectedBatchId) {
      alert('Please select an exam batch. Every response must belong to a batch.');
      return;
    }

    // 校区的写法在保存时再定一次：点校区按钮时本模板的已有记录可能还没加载完
    const groupCampus = findCampusForGroup(group, campuses);
    const groupToSave = groupCampus ? groupSpellingForCampus(groupCampus, availableResponses) : group.trim();
    if (groupToSave !== group) setGroup(groupToSave);

    // 报告和排名都按 group 分组，没有 group 的 response 会掉出排名
    if (!groupToSave) {
      alert('Please select a Campus / Group. The response cannot be saved without one.');
      return;
    }

    setIsLoading(true);
    try {
      const templateType = templateData?.templateType || 'mockTest';
      let dataToSave = prepareSubmissionData();

      if (templateType === 'finalExam') {
        // Final Exam: save with final exam format metadata
        dataToSave = {
          ...dataToSave,
          examTemplateId: selectedTemplateId,
          studentName: studentName,
          recordTime: new Date().toISOString(),
          recorder: auth.user?.profile?.name || auth.user?.profile?.email || 'Unknown',
          group: groupToSave
        };
      } else {
        // Mock Test: save both formats
        // 1. Save original mock test format in testResponse
        const mockTestData = isCurrentResponseMockFormat() ? testResponse : convertFinalExamToMockTestFormat();
        
        // 2. Save final exam format in reportTestResponse
        const reportTestData = isCurrentResponseMockFormat() ? convertMockTestToFinalExamFormat() : testResponse;
        
        dataToSave = {
          ...mockTestData,
          group: groupToSave,
          reportTestResponse: {
            ...reportTestData,
            group: groupToSave,
            studentName: studentName,
            recordTime: new Date().toISOString(),
            recorder: auth.user?.profile?.name || auth.user?.profile?.email || 'Unknown',
            examTemplateId: selectedTemplateId
          }
        };
      }
      
      // 计算学生得分
      const scoreInfo = calculateStudentScore();
      const sectionDetails = Object.entries(scoreInfo.sections)
        .map(([sectionName, data]) => `${sectionName}: ${data.current}/${data.max} (${data.percentage}%)`)
        .join('\n');
        
      if (isExistingResponse) {
        await testDataAPI.updateTestData(
          testDataId,
          studentName,
          dataToSave,
          auth.user?.id_token,
          { batchId: selectedBatchId || undefined }
        );
        alert(`Test response updated successfully!\n\nStudent: ${studentName}\nGroup: ${groupToSave || 'No Group'}\nTotal score: ${scoreInfo.totalScore}/${scoreInfo.maxScore} (${scoreInfo.percentage}%)\n\nSection scores:\n${sectionDetails}`);
      } else {
        const createdResponse = await testDataAPI.createTestData(
          studentName,
          dataToSave,
          selectedTemplateId,
          auth.user?.id_token,
          { batchId: selectedBatchId || undefined }
        );
        
        const newTestDataId = createdResponse.id;
        setTestDataId(newTestDataId);
        
        const newUrl = new URL(window.location);
        newUrl.searchParams.set('id', newTestDataId);
        window.history.replaceState({}, '', newUrl);
        setIsExistingResponse(true);
        alert(`Test response created successfully!\n\nStudent: ${studentName}\nGroup: ${groupToSave || 'No Group'}\nTotal score: ${scoreInfo.totalScore}/${scoreInfo.maxScore} (${scoreInfo.percentage}%)\n\nSection scores:\n${sectionDetails}`);
      }
    } catch (error) {
      console.error('Error saving test response:', error);
      alert('Error saving test response. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // 返回 Dashboard
  const goBack = () => {
    navigate(dashboardPath);
  };

  // Group 按钮：能对上校区就高亮那个校区；有值但对不上（All Students 等）或刚点了 Other，就高亮 Other
  const matchedGroupCampus = findCampusForGroup(group, campuses);
  const groupChipValue = matchedGroupCampus
    ? matchedGroupCampus.id
    : ((isOtherGroup || group.trim()) ? OTHER_GROUP : '');

  const handleGroupChipChange = (chipValue) => {
    if (chipValue === OTHER_GROUP) {
      setIsOtherGroup(true);
      if (matchedGroupCampus) setGroup('');
      return;
    }
    setIsOtherGroup(false);
    const campus = campuses.find(c => c.id === chipValue);
    // 写法跟随本模板已有记录，避免同一校区在报告里被拆成两组，见 groupSpellingForCampus
    setGroup(campus ? groupSpellingForCampus(campus, availableResponses) : '');
  };

  const handleOtherGroupChange = (name) => {
    const trimmed = String(name || '').trim();
    const campus = findCampusForGroup(trimmed, campuses);
    setGroup(campus ? groupSpellingForCampus(campus, availableResponses) : trimmed);
  };

  // Toggle section collapse
  const toggleSectionCollapse = (sectionType) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionType]: !prev[sectionType]
    }));
  };

  // Exclusion helper functions
  const toggleSectionExclusion = (sectionType) => {
    const urlTemplateType = searchParams.get('templateType');
    if (urlTemplateType === 'mockTest') return; // Prevent exclusion for mock tests
    
    setExcludedSections(prev => ({
      ...prev,
      [sectionType]: !prev[sectionType]
    }));
  };

  const toggleQuestionExclusion = (sectionType, questionNumber) => {
    const urlTemplateType = searchParams.get('templateType');
    if (urlTemplateType === 'mockTest') return; // Prevent exclusion for mock tests
    
    const key = `${sectionType}-${questionNumber}`;
    setExcludedQuestions(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const isSectionExcluded = (sectionType) => {
    const urlTemplateType = searchParams.get('templateType');
    if (urlTemplateType === 'mockTest') return false; // Always return false for mock tests
    
    return excludedSections[sectionType] || false;
  };

  const isQuestionExcluded = (sectionType, questionNumber) => {
    const urlTemplateType = searchParams.get('templateType');
    if (urlTemplateType === 'mockTest') return false; // Always return false for mock tests
    
    const key = `${sectionType}-${questionNumber}`;
    return excludedQuestions[key] || false;
  };

  // Load exclusion states from saved response data
  const loadExclusionStatesFromResponse = (responseData, templateData) => {
    const templateType = templateData?.templateType || 'mockTest';
    const newExcludedSections = {};
    const newExcludedQuestions = {};

    if (templateType === 'finalExam') {
      // Final Exam: Check responses array for exclusion flags
      const responses = responseData.responses || [];
      responses.forEach(response => {
        if (response.excluded) {
          if (response.questionNumber === 'section_excluded') {
            newExcludedSections[response.section] = true;
          } else {
            const key = `${response.section}-${response.questionNumber}`;
            newExcludedQuestions[key] = true;
          }
        }
      });
    } else {
      // Mock Test: Check section and question exclusion flags
      Object.entries(responseData).forEach(([sectionType, sectionData]) => {
        if (sectionData?.sectionExcluded) {
          newExcludedSections[sectionType] = true;
        }
        if (sectionData?.excludedQuestions) {
          sectionData.excludedQuestions.forEach(questionNumber => {
            const key = `${sectionType}-${questionNumber}`;
            newExcludedQuestions[key] = true;
          });
        }
        // Check subjective questions for exclusion flags
        Object.entries(sectionData || {}).forEach(([key, value]) => {
          if (key.startsWith('question_') && value?.excluded) {
            const questionNumber = key.replace('question_', '');
            const questionKey = `${sectionType}-${questionNumber}`;
            newExcludedQuestions[questionKey] = true;
          }
        });
      });
    }

    setExcludedSections(newExcludedSections);
    setExcludedQuestions(newExcludedQuestions);
  };

  // Convert mock test format to final exam format for saving
  const convertMockTestToFinalExamFormat = () => {
    const responses = [];
    
    Object.entries(testResponse).forEach(([sectionType, sectionData]) => {
      if (!sectionData || typeof sectionData !== 'object' || sectionType === 'group') return;
      
      const section = templateData.sections[sectionType];
      if (!section) return;
      
      // Add section comment if exists
      if (sectionData.sectionComment) {
        responses.push({
          section: sectionType,
          questionNumber: 'section_comment',
          response: null,
          isCorrect: true,
          score: 0,
          comment: sectionData.sectionComment
        });
      }
      
      // Process each question in the section
      section.questions.forEach(question => {
        const questionNumber = question.questionNumber;
        
        if (question.questionType === 'Subjective') {
          // Handle subjective questions
          const subjectiveData = sectionData[`question_${questionNumber}`];
          if (subjectiveData) {
            responses.push({
              section: sectionType,
              questionNumber: questionNumber,
              response: null,
              isCorrect: subjectiveData.score >= (question.score || 1),
              score: subjectiveData.score || 0,
              comment: subjectiveData.feedback || ''
            });
          }
        } else {
          // Handle objective questions
          const incorrectQuestions = sectionData.incorrectQuestions || [];
          const isIncorrect = incorrectQuestions.includes(questionNumber.toString()) || incorrectQuestions.includes(questionNumber);
          
          responses.push({
            section: sectionType,
            questionNumber: questionNumber,
            response: null,
            isCorrect: !isIncorrect,
            score: isIncorrect ? 0 : (question.score || 1),
            comment: ''
          });
        }
      });
    });
    
    return { responses };
  };

  // Convert final exam format back to mock test format
  const convertFinalExamToMockTestFormat = () => {
    const mockTestData = {};
    const responses = testResponse.responses || [];
    
    // Initialize sections
    Object.keys(templateData.sections).forEach(sectionType => {
      mockTestData[sectionType] = {
        sectionComment: '',
        excludedQuestions: [],
        sectionExcluded: false,
        incorrectQuestions: []
      };
      
      // Initialize subjective questions
      const section = templateData.sections[sectionType];
      const subjectiveQuestions = section.questions.filter(q => q.questionType === 'Subjective');
      subjectiveQuestions.forEach(question => {
        mockTestData[sectionType][`question_${question.questionNumber}`] = {
          score: 0,
          feedback: '',
          excluded: false
        };
      });
    });
    
    // Process responses
    responses.forEach(response => {
      const { section, questionNumber, isCorrect, score, comment } = response;
      
      if (!mockTestData[section]) return;
      
      if (questionNumber === 'section_comment') {
        // Handle section comments
        mockTestData[section].sectionComment = comment || '';
      } else if (questionNumber === 'section_excluded') {
        // Handle section exclusions
        mockTestData[section].sectionExcluded = response.excluded || false;
      } else {
        // Find the question in template
        const sectionTemplate = templateData.sections[section];
        const question = sectionTemplate?.questions.find(q => q.questionNumber.toString() === questionNumber.toString());
        
        if (question) {
          if (question.questionType === 'Subjective') {
            // Handle subjective questions
            mockTestData[section][`question_${questionNumber}`] = {
              score: score || 0,
              feedback: comment || '',
              excluded: response.excluded || false
            };
          } else {
            // Handle objective questions
            if (!isCorrect) {
              mockTestData[section].incorrectQuestions.push(questionNumber.toString());
            }
            if (response.excluded) {
              mockTestData[section].excludedQuestions.push(questionNumber.toString());
            }
          }
        }
      }
    });
    
    return mockTestData;
  };

  // Prepare submission data with exclusion flags
  const prepareSubmissionData = () => {
    const templateType = templateData?.templateType || 'mockTest';
    const urlTemplateType = searchParams.get('templateType');
    const isMockTest = urlTemplateType === 'mockTest';
    
    if (templateType === 'finalExam') {
      // Final Exam: Add exclusion flags to responses array
      const responses = [...(testResponse.responses || [])];
      
      // Handle section exclusion entries
      Object.entries(templateData.sections).forEach(([sectionName, section]) => {
        const sectionIsExcluded = isSectionExcluded(sectionName);
        const existingSectionExclusionIndex = responses.findIndex(
          r => r.section === sectionName && r.questionNumber === 'section_excluded'
        );

        if (sectionIsExcluded) {
          // Add section exclusion if it doesn't exist
          if (existingSectionExclusionIndex === -1) {
            responses.push({
              section: sectionName,
              questionNumber: 'section_excluded',
              response: null,
              isCorrect: false,
              score: 0,
              comment: '',
              excluded: true
            });
          }
        } else {
          // Remove section exclusion if it exists and section is no longer excluded
          if (existingSectionExclusionIndex > -1) {
            responses.splice(existingSectionExclusionIndex, 1);
          }
        }

        // Handle question exclusion entries
        section.questions.forEach(question => {
          const questionIsExcluded = isQuestionExcluded(sectionName, question.questionNumber);
          const existingIndex = responses.findIndex(
            r => r.section === sectionName && r.questionNumber === question.questionNumber
          );

          if (existingIndex > -1) {
            // Update existing response exclusion status
            responses[existingIndex].excluded = questionIsExcluded;
          } else if (questionIsExcluded) {
            // Add new excluded question entry only if it's excluded
            responses.push({
              section: sectionName,
              questionNumber: question.questionNumber,
              response: null,
              isCorrect: false,
              score: 0,
              comment: '',
              excluded: true
            });
          }
        });
      });
      
      return {
        ...testResponse,
        responses
      };
    } else {
      // Mock Test: Return data as-is for mock tests (will be handled in save function)
      if (isMockTest) {
        return testResponse;
      }
      
      // For other template types: Add exclusion fields to section data
      const preparedData = { ...testResponse };
      
      Object.entries(templateData.sections).forEach(([sectionName, section]) => {
        if (!preparedData[sectionName]) {
          preparedData[sectionName] = {};
        }
        
        // Set section exclusion flag
        preparedData[sectionName].sectionExcluded = isSectionExcluded(sectionName);
        
        // Collect excluded questions and update exclusion status
        const excludedQuestions = [];
        section.questions.forEach(question => {
          const questionIsExcluded = isQuestionExcluded(sectionName, question.questionNumber);

          if (questionIsExcluded) {
            excludedQuestions.push(question.questionNumber);
          }

          // For subjective questions, always update exclusion flag
          if (question.questionType === 'Subjective') {
            const questionKey = `question_${question.questionNumber}`;
            if (!preparedData[sectionName][questionKey]) {
              preparedData[sectionName][questionKey] = { score: 0, feedback: '', excluded: false };
            }
            preparedData[sectionName][questionKey].excluded = questionIsExcluded;
          }
        });
        
        preparedData[sectionName].excludedQuestions = excludedQuestions;
      });
      
      return preparedData;
    }
  };

  // Scroll to section with proper offset and timing
  const scrollToSection = (elementId) => {
    setTimeout(() => {
      const element = document.getElementById(elementId);
      if (element) {
        const topOffset = 100; // 预留顶部空间
        const bottomOffset = 200; // 预留底部空间
        
        const elementRect = element.getBoundingClientRect();
        const absoluteElementTop = elementRect.top + window.pageYOffset;
        const windowHeight = window.innerHeight;
        const documentHeight = document.documentElement.scrollHeight;
        
        let targetPosition = absoluteElementTop - topOffset;
        
        const maxScrollPosition = documentHeight - windowHeight;
        if (targetPosition > maxScrollPosition - bottomOffset) {
          targetPosition = Math.max(0, absoluteElementTop - windowHeight + elementRect.height + bottomOffset);
        }
        
        targetPosition = Math.max(0, Math.min(targetPosition, maxScrollPosition));
        
        window.scrollTo({
          top: targetPosition,
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  const jumpToResponse = async (responseId) => {
    const responseIndex = availableResponses.findIndex(r => r.id === responseId);
    if (responseIndex >= 0) {
      // Preserve templateType parameter in URL and use React Router navigate
      const templateType = searchParams.get('templateType');
      const newPath = templateType
        ? `${testResponsePath}?id=${responseId}&templateType=${templateType}`
        : `${testResponsePath}?id=${responseId}`;
      navigate(newPath, { replace: true });
    }
  };

  // 计算学生得分 - Mock Test format
  const calculateMockTestScore = () => {
    if (!templateData) return { totalScore: 0, maxScore: 0, sections: {}, excluded: { sections: 0, questions: 0 } };
    
    const templateType = templateData?.templateType || 'mockTest';
    const hideExclusions = templateType === 'mockTest';
    
    let totalScore = 0;
    let maxScore = 0;
    let excludedSectionsCount = 0;
    let excludedQuestionsCount = 0;
    const sectionScores = {};
    
    Object.entries(templateData.sections).forEach(([sectionName, section]) => {
      let sectionMaxScore = 0;
      let sectionCurrentScore = 0;
      
      // Skip exclusion logic for mock test templates
      if (!hideExclusions) {
        // Check if entire section is excluded
        if (isSectionExcluded(sectionName)) {
          excludedSectionsCount++;
          sectionScores[sectionName] = {
            current: 0,
            max: 0,
            percentage: 'Excluded',
            excluded: true
          };
          return;
        }
      }
      
      section.questions.forEach(question => {
        const questionScore = question.score || 0;
        const questionIsExcluded = !hideExclusions && isQuestionExcluded(sectionName, question.questionNumber);
        
        if (questionIsExcluded) {
          excludedQuestionsCount++;
          return;
        }
        
        sectionMaxScore += questionScore;
        
        if (question.questionType === 'Subjective') {
          // 主观题使用评分 - handle both formats
          let subjectiveScore = 0;
          if (isCurrentResponseMockFormat()) {
            const subjectiveResponse = testResponse[sectionName]?.[`question_${question.questionNumber}`];
            subjectiveScore = subjectiveResponse?.score || 0;
          } else {
            const response = getFinalExamResponse(sectionName, question.questionNumber);
            subjectiveScore = response.score || 0;
          }
          sectionCurrentScore += subjectiveScore;
        } else {
          // 客观题检查是否错题 - handle both formats
          let isIncorrect = false;
          if (isCurrentResponseMockFormat()) {
            const incorrectQuestions = testResponse[sectionName]?.incorrectQuestions || [];
            isIncorrect = incorrectQuestions.includes(question.questionNumber.toString()) || incorrectQuestions.includes(question.questionNumber);
          } else {
            const response = getFinalExamResponse(sectionName, question.questionNumber);
            isIncorrect = !response.isCorrect;
          }
          if (!isIncorrect) {
            sectionCurrentScore += questionScore;
          }
        }
      });
      
      sectionScores[sectionName] = {
        current: sectionCurrentScore,
        max: sectionMaxScore,
        percentage: sectionMaxScore > 0 ? (sectionCurrentScore / sectionMaxScore * 100).toFixed(1) : 0,
        excluded: false
      };
      
      totalScore += sectionCurrentScore;
      maxScore += sectionMaxScore;
    });
    
    return {
      totalScore,
      maxScore,
      percentage: maxScore > 0 ? (totalScore / maxScore * 100).toFixed(1) : 0,
      sections: sectionScores,
      excluded: { sections: excludedSectionsCount, questions: excludedQuestionsCount }
    };
  };

  // 计算学生得分 - Final Exam
  const calculateFinalExamScore = () => {
    if (!templateData) return { totalScore: 0, maxScore: 0, sections: {}, excluded: { sections: 0, questions: 0 } };
    
    let totalScore = 0;
    let maxScore = 0;
    let excludedSectionsCount = 0;
    let excludedQuestionsCount = 0;
    const sectionScores = {};
    
    Object.entries(templateData.sections).forEach(([sectionName, section]) => {
      let sectionMaxScore = 0;
      let sectionCurrentScore = 0;
      
      // Check if entire section is excluded
      if (isSectionExcluded(sectionName)) {
        excludedSectionsCount++;
        sectionScores[sectionName] = {
          current: 0,
          max: 0,
          percentage: 'Excluded',
          excluded: true
        };
        return;
      }
      
      section.questions.forEach(question => {
        const questionScore = question.score || 0;
        const questionIsExcluded = isQuestionExcluded(sectionName, question.questionNumber);
        
        if (questionIsExcluded) {
          excludedQuestionsCount++;
          return;
        }
        
        sectionMaxScore += questionScore;
        
        const response = getFinalExamResponse(sectionName, question.questionNumber);
        
        if (question.questionType === 'Subjective') {
          // 主观题使用给定分数
          sectionCurrentScore += response.score || 0;
        } else {
          // 客观题检查是否正确
          if (response.isCorrect) {
            sectionCurrentScore += questionScore;
          }
        }
      });
      
      sectionScores[sectionName] = {
        current: sectionCurrentScore,
        max: sectionMaxScore,
        percentage: sectionMaxScore > 0 ? (sectionCurrentScore / sectionMaxScore * 100).toFixed(1) : 0,
        excluded: false
      };
      
      totalScore += sectionCurrentScore;
      maxScore += sectionMaxScore;
    });
    
    return {
      totalScore,
      maxScore,
      percentage: maxScore > 0 ? (totalScore / maxScore * 100).toFixed(1) : 0,
      sections: sectionScores,
      excluded: { sections: excludedSectionsCount, questions: excludedQuestionsCount }
    };
  };

  // 通用得分计算函数
  const calculateStudentScore = () => {
    const templateType = templateData?.templateType || 'mockTest';
    
    if (templateType === 'finalExam') {
      return calculateFinalExamScore();
    } else {
      return calculateMockTestScore();
    }
  };  // 渲染 Mock Test 选择题部分（Objective类型）
  const renderMockTestObjectiveSection = (sectionType, section) => {
    const objectiveQuestions = section.questions.filter(q => q.questionType === 'Objective' || !q.questionType); // 兼容没有questionType的旧数据
    
    if (objectiveQuestions.length === 0) return null;
    
    // Get incorrect questions based on current format
    const incorrectQuestions = isCurrentResponseMockFormat() 
      ? getMockTestIncorrectQuestions(sectionType)
      : (testResponse.responses || [])
          .filter(r => r.section === sectionType && !r.isCorrect)
          .map(r => r.questionNumber);
    
    const sectionIsExcluded = isSectionExcluded(sectionType);
    // Hide exclusions for mock test templates
    const templateType = templateData?.templateType || 'mockTest';
    const hideExclusions = templateType === 'mockTest';

    return (
      <S.FormSection key={`${sectionType}-objective`} id={`section-${sectionType}-objective`} style={{
        opacity: sectionIsExcluded ? 0.5 : 1,
        background: sectionIsExcluded ? '#f8f9fa' : '',
        border: sectionIsExcluded ? '2px dashed #6c757d' : ''
      }}>
        <S.CollapsibleSectionHeader>
          {!hideExclusions && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '14px', color: '#6c757d' }}>
                <input
                  type="checkbox"
                  checked={sectionIsExcluded}
                  onChange={() => toggleSectionExclusion(sectionType)}
                />
                Exclude entire section
              </label>
            </div>
          )}
          <S.SectionTitle onClick={() => toggleSectionCollapse(`${sectionType}-objective`)}>
            <S.CollapseIcon collapsed={collapsedSections[`${sectionType}-objective`]}>
              ▼
            </S.CollapseIcon>
            {sectionType} Section - Mark Incorrect Questions {objectiveQuestions.length < section.questions.length ? '(Objective)' : ''}
          </S.SectionTitle>
        </S.CollapsibleSectionHeader>
        
        {!collapsedSections[`${sectionType}-objective`] && (
          <>
            <p>Click on question numbers to mark them as incorrect:</p>
            <S.QuestionGrid>
              {objectiveQuestions.map((question) => {
                const questionIsExcluded = isQuestionExcluded(sectionType, question.questionNumber);
                const questionIsIncorrect = incorrectQuestions.includes(question.questionNumber.toString()) || incorrectQuestions.includes(question.questionNumber);
                
                return (
                  <div key={question.questionNumber} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
                    {!hideExclusions && (
                      <label style={{ display: 'flex', alignItems: 'center', gap: '3px', fontSize: '12px', color: '#6c757d' }}>
                        <input
                          type="checkbox"
                          checked={questionIsExcluded}
                          onChange={() => toggleQuestionExclusion(sectionType, question.questionNumber)}
                          disabled={sectionIsExcluded}
                        />
                        Exclude
                      </label>
                    )}
                    <S.QuestionButton
                      incorrect={questionIsIncorrect}
                      onClick={() => !sectionIsExcluded && !questionIsExcluded && toggleIncorrectQuestion(sectionType, question.questionNumber)}
                      id={`section-${sectionType}-question-${question.questionNumber}`}
                      style={{
                        opacity: (sectionIsExcluded || questionIsExcluded) ? 0.5 : 1,
                        cursor: (sectionIsExcluded || questionIsExcluded) ? 'not-allowed' : 'pointer',
                        background: questionIsExcluded ? '#f8f9fa' : ''
                      }}
                      disabled={sectionIsExcluded || questionIsExcluded}
                    >
                      Q{question.questionNumber}
                      {questionIsIncorrect && ' ✗'}
                      {questionIsExcluded && ' (Excluded)'}
                    </S.QuestionButton>
                  </div>
                );
              })}
            </S.QuestionGrid>
            <div style={{ marginTop: '10px', fontSize: '14px', color: '#6c757d' }}>
              Incorrect questions: {incorrectQuestions.length > 0 ? incorrectQuestions.join(', ') : 'None'}
            </div>
            
            {/* Section Comment */}
            <S.FormGroup style={{ marginTop: '20px' }}>
              <S.Label>Section Comment (Optional)</S.Label>
              <S.TextArea
                value={getSectionComment(sectionType)}
                onChange={(e) => updateSectionComment(sectionType, e.target.value)}
                placeholder={`Enter overall comment for ${sectionType} section...`}
                rows="3"
              />
            </S.FormGroup>
          </>
        )}      </S.FormSection>
    );
  };

  // 渲染 Mock Test 主观题部分（Subjective类型）
  const renderMockTestSubjectiveSection = (sectionType, section) => {
    const subjectiveQuestions = section.questions.filter(q => q.questionType === 'Subjective');
    
    if (subjectiveQuestions.length === 0) return null;
    
    const sectionIsExcluded = isSectionExcluded(sectionType);
    // Hide exclusions for mock test templates
    const templateType = templateData?.templateType || 'mockTest';
    const hideExclusions = templateType === 'mockTest';

    return (
      <S.FormSection key={`${sectionType}-subjective`} id={`section-${sectionType}-subjective`} style={{
        opacity: sectionIsExcluded ? 0.5 : 1,
        background: sectionIsExcluded ? '#f8f9fa' : '',
        border: sectionIsExcluded ? '2px dashed #6c757d' : ''
      }}>
        <S.CollapsibleSectionHeader>
          {!hideExclusions && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '14px', color: '#6c757d' }}>
                <input
                  type="checkbox"
                  checked={sectionIsExcluded}
                  onChange={() => toggleSectionExclusion(sectionType)}
                />
                Exclude entire section
              </label>
            </div>
          )}
          <S.SectionTitle onClick={() => toggleSectionCollapse(`${sectionType}-subjective`)}>
            <S.CollapseIcon collapsed={collapsedSections[`${sectionType}-subjective`]}>
              ▼
            </S.CollapseIcon>
            {sectionType} Section - Scoring (Subjective)
          </S.SectionTitle>
        </S.CollapsibleSectionHeader>
        
        {!collapsedSections[`${sectionType}-subjective`] && (
          <>
            {subjectiveQuestions.map((question) => {
              // Get question response based on current format
              const questionResponse = isCurrentResponseMockFormat()
                ? getMockTestSubjectiveData(sectionType, question.questionNumber)
                : (() => {
                    const resp = getFinalExamResponse(sectionType, question.questionNumber);
                    return { score: resp.score || 0, feedback: resp.comment || '' };
                  })();
              
              const questionIsExcluded = isQuestionExcluded(sectionType, question.questionNumber);
              
              return (
                <S.WritingQuestionContainer key={question.questionNumber} id={`section-${sectionType}-question-${question.questionNumber}`} style={{
                  opacity: (sectionIsExcluded || questionIsExcluded) ? 0.5 : 1,
                  background: questionIsExcluded ? '#f8f9fa' : '',
                  border: questionIsExcluded ? '2px dashed #6c757d' : ''
                }}>
                  <S.QuestionHeader>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                      <div>
                        <span>Question {question.questionNumber}</span>
                        <span style={{ marginLeft: '20px' }}>Max Score: {question.score}</span>
                      </div>
                      {!hideExclusions && (
                        <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '14px', color: '#6c757d' }}>
                          <input
                            type="checkbox"
                            checked={questionIsExcluded}
                            onChange={() => toggleQuestionExclusion(sectionType, question.questionNumber)}
                            disabled={sectionIsExcluded}
                          />
                          Exclude question
                        </label>
                      )}
                    </div>
                  </S.QuestionHeader>
                  <S.QuestionContent>
                    <S.Row>
                      <S.Col flex="1">
                        <S.FormGroup>
                          <S.Label>Score</S.Label>
                          <S.Input
                            type="number"
                            min="0"
                            max={question.score}
                            step="0.5"
                            value={questionResponse.score}
                            onChange={(e) => updateSectionWritingScore(sectionType, question.questionNumber, 'score', parseFloat(e.target.value) || 0)}
                            disabled={sectionIsExcluded || questionIsExcluded}
                          />
                        </S.FormGroup>
                      </S.Col>
                      <S.Col flex="3">
                        <S.FormGroup>
                          <S.Label>Feedback</S.Label>
                          <S.TextArea
                            value={questionResponse.feedback}
                            onChange={(e) => updateSectionWritingScore(sectionType, question.questionNumber, 'feedback', e.target.value)}
                            placeholder="Enter feedback for this question..."
                            disabled={sectionIsExcluded || questionIsExcluded}
                          />
                        </S.FormGroup>
                      </S.Col>
                    </S.Row>
                    <div style={{ fontSize: '14px', color: '#6c757d', marginTop: '10px' }}>
                      <strong>Question Description:</strong> {question.meta?.description || 'No description provided'}
                    </div>                  </S.QuestionContent>
                </S.WritingQuestionContainer>
              );
            })}
            
            {/* Section Comment */}
            <S.FormGroup style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #e9ecef' }}>
              <S.Label>Section Comment (Optional)</S.Label>
              <S.TextArea
                value={getSectionComment(sectionType)}
                onChange={(e) => updateSectionComment(sectionType, e.target.value)}
                placeholder={`Enter overall comment for ${sectionType} section...`}
                rows="3"
              />
            </S.FormGroup>
          </>
        )}
      </S.FormSection>
    );
  };
  // 渲染 Final Exam 选择题部分（Objective类型）
  const renderFinalExamObjectiveSection = (sectionType, section) => {
    const incorrectQuestions = (testResponse.responses || [])
      .filter(r => r.section === sectionType && !r.isCorrect)
      .map(r => r.questionNumber);
    
    const objectiveQuestions = section.questions.filter(q => q.questionType === 'Objective');
    
    if (objectiveQuestions.length === 0) return null;
    
    const sectionIsExcluded = isSectionExcluded(sectionType);

    return (
      <S.FormSection key={`${sectionType}-objective`} id={`section-${sectionType}-objective`} style={{
        opacity: sectionIsExcluded ? 0.5 : 1,
        background: sectionIsExcluded ? '#f8f9fa' : '',
        border: sectionIsExcluded ? '2px dashed #6c757d' : ''
      }}>
        <S.CollapsibleSectionHeader>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '14px', color: '#6c757d' }}>
              <input
                type="checkbox"
                checked={sectionIsExcluded}
                onChange={() => toggleSectionExclusion(sectionType)}
              />
              Exclude entire section
            </label>
          </div>
          <S.SectionTitle onClick={() => toggleSectionCollapse(`${sectionType}-objective`)}>
            <S.CollapseIcon collapsed={collapsedSections[`${sectionType}-objective`]}>
              ▼
            </S.CollapseIcon>
            {sectionType} Section - Mark Incorrect Questions (Objective)
          </S.SectionTitle>
        </S.CollapsibleSectionHeader>
        
        {!collapsedSections[`${sectionType}-objective`] && (
          <>
            <p>Click on question numbers to mark them as incorrect:</p>
            <S.QuestionGrid>
              {objectiveQuestions.map((question) => {
                const response = getFinalExamResponse(sectionType, question.questionNumber);
                const isIncorrect = !response.isCorrect;
                const questionIsExcluded = isQuestionExcluded(sectionType, question.questionNumber);
                
                return (
                  <div key={question.questionNumber} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '3px', fontSize: '12px', color: '#6c757d' }}>
                      <input
                        type="checkbox"
                        checked={questionIsExcluded}
                        onChange={() => toggleQuestionExclusion(sectionType, question.questionNumber)}
                        disabled={sectionIsExcluded}
                      />
                      Exclude
                    </label>
                    <S.QuestionButton
                      incorrect={isIncorrect}
                      onClick={() => !sectionIsExcluded && !questionIsExcluded && toggleFinalExamIncorrectQuestion(sectionType, question.questionNumber)}
                      id={`section-${sectionType}-question-${question.questionNumber}`}
                      style={{
                        opacity: (sectionIsExcluded || questionIsExcluded) ? 0.5 : 1,
                        cursor: (sectionIsExcluded || questionIsExcluded) ? 'not-allowed' : 'pointer',
                        background: questionIsExcluded ? '#f8f9fa' : ''
                      }}
                      disabled={sectionIsExcluded || questionIsExcluded}
                    >
                      Q{question.questionNumber}
                      {isIncorrect && ' ✗'}
                      {questionIsExcluded && ' (Excluded)'}
                    </S.QuestionButton>
                  </div>
                );
              })}
            </S.QuestionGrid>            <div style={{ marginTop: '10px', fontSize: '14px', color: '#6c757d' }}>
              Incorrect questions: {incorrectQuestions.length > 0 ? incorrectQuestions.join(', ') : 'None'}
            </div>
            
            {/* Section Comment */}
            <S.FormGroup style={{ marginTop: '20px' }}>
              <S.Label>Section Comment (Optional)</S.Label>
              <S.TextArea
                value={getSectionComment(sectionType)}
                onChange={(e) => updateSectionComment(sectionType, e.target.value)}
                placeholder={`Enter overall comment for ${sectionType} section...`}
                rows="3"
              />
            </S.FormGroup>
          </>
        )}
      </S.FormSection>
    );
  };

  // 渲染 Final Exam Subjective 部分
  const renderFinalExamSubjectiveSection = (sectionType, section) => {
    const subjectiveQuestions = section.questions.filter(q => q.questionType === 'Subjective');
    
    if (subjectiveQuestions.length === 0) return null;
    
    const sectionIsExcluded = isSectionExcluded(sectionType);

    return (
      <S.FormSection key={`${sectionType}-subjective`} id={`section-${sectionType}-subjective`} style={{
        opacity: sectionIsExcluded ? 0.5 : 1,
        background: sectionIsExcluded ? '#f8f9fa' : '',
        border: sectionIsExcluded ? '2px dashed #6c757d' : ''
      }}>
        <S.CollapsibleSectionHeader>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '14px', color: '#6c757d' }}>
              <input
                type="checkbox"
                checked={sectionIsExcluded}
                onChange={() => toggleSectionExclusion(sectionType)}
              />
              Exclude entire section
            </label>
          </div>
          <S.SectionTitle onClick={() => toggleSectionCollapse(`${sectionType}-subjective`)}>
            <S.CollapseIcon collapsed={collapsedSections[`${sectionType}-subjective`]}>
              ▼
            </S.CollapseIcon>
            {sectionType} Section - Scoring (Subjective)
          </S.SectionTitle>
        </S.CollapsibleSectionHeader>
        
        {!collapsedSections[`${sectionType}-subjective`] && (
          <>
            {subjectiveQuestions.map((question) => {
              const response = getFinalExamResponse(sectionType, question.questionNumber);
              const questionIsExcluded = isQuestionExcluded(sectionType, question.questionNumber);
              
              return (
                <S.WritingQuestionContainer key={question.questionNumber} id={`section-${sectionType}-question-${question.questionNumber}`} style={{
                  opacity: (sectionIsExcluded || questionIsExcluded) ? 0.5 : 1,
                  background: questionIsExcluded ? '#f8f9fa' : '',
                  border: questionIsExcluded ? '2px dashed #6c757d' : ''
                }}>
                  <S.QuestionHeader>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                      <div>
                        <span>Question {question.questionNumber}{question.questionName ? ` - ${question.questionName}` : ''}</span>
                        <span style={{ marginLeft: '20px' }}>Max Score: {question.score}</span>
                      </div>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '14px', color: '#6c757d' }}>
                        <input
                          type="checkbox"
                          checked={questionIsExcluded}
                          onChange={() => toggleQuestionExclusion(sectionType, question.questionNumber)}
                          disabled={sectionIsExcluded}
                        />
                        Exclude question
                      </label>
                    </div>
                  </S.QuestionHeader>
                  <S.QuestionContent>
                    <S.Row>
                      <S.Col flex="1">
                        <S.FormGroup>
                          <S.Label>Score</S.Label>
                          <S.Input
                            type="number"
                            min="0"
                            max={question.score}
                            step="0.5"
                            value={response.score || 0}
                            onChange={(e) => updateFinalExamResponse(sectionType, question.questionNumber, 'score', parseFloat(e.target.value) || 0)}
                            disabled={sectionIsExcluded || questionIsExcluded}
                          />
                        </S.FormGroup>
                      </S.Col>
                      <S.Col flex="3">
                        <S.FormGroup>
                          <S.Label>Feedback (Optional)</S.Label>
                          <S.TextArea
                            value={response.comment || ''}
                            onChange={(e) => updateFinalExamResponse(sectionType, question.questionNumber, 'comment', e.target.value)}
                            placeholder="Enter feedback for this question (optional)..."
                            disabled={sectionIsExcluded || questionIsExcluded}
                          />
                        </S.FormGroup>
                      </S.Col>
                    </S.Row>                  </S.QuestionContent>
                </S.WritingQuestionContainer>
              );
            })}
            
            {/* Section Comment */}
            <S.FormGroup style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #e9ecef' }}>
              <S.Label>Section Comment (Optional)</S.Label>
              <S.TextArea
                value={getSectionComment(sectionType)}
                onChange={(e) => updateSectionComment(sectionType, e.target.value)}
                placeholder={`Enter overall comment for ${sectionType} section...`}
                rows="3"
              />
            </S.FormGroup>
          </>
        )}
      </S.FormSection>
    );
  };

  return (
    <S.Container>
      {isLoading && (
        <div style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          background: 'rgba(0,0,0,0.5)', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          zIndex: 1000,
          color: 'white',
          fontSize: '18px'
        }}>
          Loading...
        </div>
      )}

      {/* Navigation Sidebar */}
      {showNavigation && (
        <S.NavigationSidebar>
          <S.NavigationHeader>
            <h4>Navigation</h4>
            <S.ToggleButton onClick={() => setShowNavigation(false)}>
              ←
            </S.ToggleButton>
          </S.NavigationHeader>
          <S.NavigationContent>
            <S.NavigationItem onClick={() => scrollToSection('section-basic-info')}>
              📋 Basic Information
            </S.NavigationItem>            {templateData &&
              (() => {
                const templateType = templateData.templateType || 'mockTest';
                if (templateType === 'finalExam') {
                  // 动态获取template中定义的sections，而不是使用固定的顺序
                  return Object.entries(templateData.sections).map(([sectionType, section]) => {
                    if (!section || section.questions.length === 0) return null;
                    const objectiveQuestions = section.questions.filter(q => q.questionType === 'Objective');
                    const subjectiveQuestions = section.questions.filter(q => q.questionType === 'Subjective');
                    
                    return (
                      <S.NavigationSection key={sectionType}>
                        {objectiveQuestions.length > 0 && (
                          <>
                            <S.NavigationItem 
                              onClick={() => scrollToSection(`section-${sectionType}-objective`)}
                              isSection
                            >
                              📁 {sectionType} (Objective) ({objectiveQuestions.length})
                            </S.NavigationItem>
                            {objectiveQuestions.map((question) => (
                              <S.NavigationSubItem
                                key={question.questionNumber}
                                onClick={() => scrollToSection(`section-${sectionType}-question-${question.questionNumber}`)}
                              >
                                📝 Q{question.questionNumber}
                                {question.questionName && ` - ${question.questionName}`}
                              </S.NavigationSubItem>
                            ))}
                          </>
                        )}
                        {subjectiveQuestions.length > 0 && (
                          <>
                            <S.NavigationItem 
                              onClick={() => scrollToSection(`section-${sectionType}-subjective`)}
                              isSection
                            >
                              📁 {sectionType} (Subjective) ({subjectiveQuestions.length})
                            </S.NavigationItem>
                            {subjectiveQuestions.map((question) => (
                              <S.NavigationSubItem
                                key={question.questionNumber}
                                onClick={() => scrollToSection(`section-${sectionType}-question-${question.questionNumber}`)}
                              >
                                📝 Q{question.questionNumber}
                                {question.questionName && ` - ${question.questionName}`}
                              </S.NavigationSubItem>
                            ))}
                          </>
                        )}                      </S.NavigationSection>
                    );
                  });
                } else {
                  // Mock Test 模式：也按照questionType来分组显示
                  return Object.entries(templateData.sections).map(([sectionType, section]) => {
                    if (!section || section.questions.length === 0) return null;
                    const objectiveQuestions = section.questions.filter(q => q.questionType === 'Objective' || !q.questionType); // 兼容没有questionType的旧数据
                    const subjectiveQuestions = section.questions.filter(q => q.questionType === 'Subjective');
                    
                    return (
                      <S.NavigationSection key={sectionType}>
                        {objectiveQuestions.length > 0 && (
                          <>
                            <S.NavigationItem 
                              onClick={() => scrollToSection(`section-${sectionType}-objective`)}
                              isSection
                            >
                              📁 {sectionType} {objectiveQuestions.length < section.questions.length ? '(Objective)' : ''} ({objectiveQuestions.length})
                            </S.NavigationItem>
                            {objectiveQuestions.map((question) => (
                              <S.NavigationSubItem
                                key={question.questionNumber}
                                onClick={() => scrollToSection(`section-${sectionType}-question-${question.questionNumber}`)}
                              >
                                📝 Q{question.questionNumber}
                                {question.questionName && ` - ${question.questionName}`}
                              </S.NavigationSubItem>
                            ))}
                          </>
                        )}
                        {subjectiveQuestions.length > 0 && (
                          <>
                            <S.NavigationItem 
                              onClick={() => scrollToSection(`section-${sectionType}-subjective`)}
                              isSection
                            >
                              📁 {sectionType} (Subjective) ({subjectiveQuestions.length})
                            </S.NavigationItem>
                            {subjectiveQuestions.map((question) => (
                              <S.NavigationSubItem
                                key={question.questionNumber}
                                onClick={() => scrollToSection(`section-${sectionType}-question-${question.questionNumber}`)}
                              >
                                📝 Q{question.questionNumber}
                                {question.questionName && ` - ${question.questionName}`}
                              </S.NavigationSubItem>
                            ))}
                          </>
                        )}
                      </S.NavigationSection>
                    );
                  });
                }
              })()
            }
          </S.NavigationContent>
        </S.NavigationSidebar>
      )}

      {/* Toggle Navigation Button */}
      {!showNavigation && (
        <S.ShowNavigationButton onClick={() => setShowNavigation(true)}>
          →
        </S.ShowNavigationButton>
      )}

      <S.MainContent showNavigation={showNavigation}>
        <S.Header>
          <div>
            <S.Title>Test Response</S.Title>
            <S.TemplateId>
              ID: {testDataId} {isExistingResponse ? '(Existing)' : '(New)'}
            </S.TemplateId>
          </div>
        </S.Header>

        <S.FloatingButtonGroup>
          <S.Button secondary onClick={goBack} disabled={isLoading}>
            Back to Dashboard
          </S.Button>
          <S.Button success onClick={saveTestResponse} disabled={isLoading || !selectedTemplateId}>
            {isLoading ? 'Saving...' : (isExistingResponse ? 'Update Response' : 'Save Response')}
          </S.Button>
        </S.FloatingButtonGroup>

        {/* Response Navigation Controls */}
        {isNavigationMode && availableResponses.length > 0 && (
          <S.FormSection style={{ background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
            <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
              {/* Response Selector */}
              <div style={{ minWidth: '200px', flex: '1' }}>
                <S.Label htmlFor="test-response-navigator">Response ({currentResponseIndex + 1} of {availableResponses.length})</S.Label>
                <SearchableSelect
                  id="test-response-navigator"
                  value={testDataId}
                  onChange={(responseId) => { if (responseId) jumpToResponse(responseId); }}
                  disabled={isLoading}
                  clearable={false}
                  placeholder="Search student name..."
                  noOptionsText="No student matches"
                  options={availableResponses.map((response, index) => ({
                    value: response.id,
                    label: `${index + 1}. ${response.studentName}`,
                    description: readResponseGroup(response) || undefined,
                  }))}
                />
              </div>

              {/* Navigation Buttons */}
              {availableResponses.length > 1 && (
                <div style={{ display: 'flex', gap: '10px' }}>
                  <S.Button 
                    onClick={() => navigateToResponse('prev')}
                    disabled={isLoading}
                    style={{ 
                      padding: '8px 16px', 
                      fontSize: '14px',
                      background: '#6c757d',
                      minWidth: 'auto'
                    }}
                  >
                    ← Previous
                  </S.Button>
                  <S.Button 
                    onClick={() => navigateToResponse('next')}
                    disabled={isLoading}
                    style={{ 
                      padding: '8px 16px', 
                      fontSize: '14px',
                      background: '#6c757d',
                      minWidth: 'auto'
                    }}
                  >
                    Next →
                  </S.Button>
                </div>
              )}

              {/* Current Response Info */}
              {currentResponseIndex >= 0 && (
                <div style={{ 
                  padding: '8px 12px', 
                  background: '#e9ecef', 
                  borderRadius: '4px',
                  fontSize: '14px',
                  color: '#495057'
                }}>
                  <strong>Navigation Mode:</strong> {availableResponses.length} responses available
                  <br />
                  <span style={{ fontSize: '12px', color: '#6c757d' }}>
                    💡 Use Ctrl+← / Ctrl+→ for keyboard navigation
                  </span>
                </div>
              )}
            </div>
          </S.FormSection>
        )}

        {/* 基本信息 */}
        <S.FormSection id="section-basic-info">
          <S.SectionTitle>Basic Information</S.SectionTitle>
          <S.Row>
            <S.Col>
              <S.FormGroup>
                <S.Label htmlFor="test-response-student-name">Student Name</S.Label>
                <S.Input
                  id="test-response-student-name"
                  value={studentName}
                  onChange={(e) => setStudentName(e.target.value)}
                  placeholder="Enter student name"
                />
                <S.FilterLine>
                  <S.FilterLabel>Class day</S.FilterLabel>
                  <ChipSelect
                    aria-label="Class day"
                    allowDeselect
                    disabled={!stripWeekdayTag(studentName).trim()}
                    options={WEEKDAYS.map(day => ({ value: day, label: day }))}
                    value={getWeekdayTag(studentName)}
                    onChange={(day) => setStudentName(setWeekdayTag(studentName, day))}
                  />
                </S.FilterLine>
                <S.FieldHint>
                  Adds the day after the name, e.g. “Alice (Mon)”, to tell apart students with the same name.
                </S.FieldHint>
              </S.FormGroup>
            </S.Col>
            <S.Col>
              <S.FormGroup>
                <S.Label htmlFor="test-response-template">
                  Test Template
                  {(() => {
                    const urlTemplateType = searchParams.get('templateType');
                    if (urlTemplateType) {
                      const displayName = urlTemplateType === 'finalExam' ? 'Final Exam' : 
                                         urlTemplateType === 'mockTest' ? 'Mock Test' : urlTemplateType;
                      return <span style={{ color: '#007bff', fontSize: '12px', marginLeft: '8px' }}>({displayName} only)</span>;
                    }
                    return null;
                  })()} 
                </S.Label>
                {!isExistingResponse && availableTemplates.length > 0 && (
                  <>
                    <S.FilterLine>
                      <S.FilterLabel>Term</S.FilterLabel>
                      <ChipSelect
                        aria-label="Filter templates by term"
                        options={termChipOptions}
                        value={effectiveTermFilter}
                        onChange={(term) => setTemplateFilter(prev => ({ ...prev, term }))}
                      />
                    </S.FilterLine>
                    <S.FilterLine>
                      <S.FilterLabel>Year</S.FilterLabel>
                      <ChipSelect
                        aria-label="Filter templates by year level"
                        options={yearChipOptions}
                        value={effectiveYearFilter}
                        onChange={(year) => setTemplateFilter(prev => ({ ...prev, year }))}
                      />
                    </S.FilterLine>
                  </>
                )}
                {/* 不给清除：换模板会清空已录的对错，只能主动选另一个 */}
                <SearchableSelect
                  id="test-response-template"
                  value={selectedTemplateId}
                  onChange={(templateId) => {
                    if (templateId && templateId !== selectedTemplateId) handleTemplateSelection(templateId);
                  }}
                  disabled={isExistingResponse}
                  clearable={false}
                  searchable
                  placeholder="Search or select a test template..."
                  noOptionsText="No template matches the filters"
                  options={templateOptions}
                />
                {!isExistingResponse && availableTemplates.length > 0 && (
                  <S.FieldHint>
                    {matchingTemplateCount} of {availableTemplates.length} templates match the filters.
                  </S.FieldHint>
                )}
                {(() => {
                  const urlTemplateType = searchParams.get('templateType');
                  if (urlTemplateType && availableTemplates.length === 0) {
                    const displayName = urlTemplateType === 'finalExam' ? 'Final Exam' : 
                                       urlTemplateType === 'mockTest' ? 'Mock Test' : urlTemplateType;
                    return (
                      <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '4px' }}>
                        No {displayName} templates available. Please create a template first.
                      </div>
                    );
                  }
                  return null;
                })()} 
              </S.FormGroup>
            </S.Col>
          </S.Row>

          {/* Group（实际就是校区）与考试批次 */}
          {selectedTemplateId && (
            <S.Row>
              <S.Col>
                <S.FormGroup>
                  <S.Label>
                    Campus / Group <span style={{ color: '#dc3545' }}>*</span>
                  </S.Label>
                  <ChipSelect
                    aria-label="Campus or group"
                    allowDeselect
                    options={[
                      ...campuses.map(campus => ({ value: campus.id, label: campus.name })),
                      { value: OTHER_GROUP, label: 'Other…' },
                    ]}
                    value={groupChipValue}
                    onChange={handleGroupChipChange}
                  />
                  {groupChipValue === OTHER_GROUP && (
                    <div style={{ marginTop: '8px' }}>
                      <SearchableSelect
                        aria-label="Other group name"
                        allowCustomValue
                        value={group}
                        onChange={handleOtherGroupChange}
                        placeholder="Type a group name, e.g. All Students"
                        noOptionsText="Type a name to use it as the group"
                        options={otherGroupOptions}
                      />
                    </div>
                  )}
                  <S.FieldHint>Required. Reports and rankings are grouped by this. Tap the selected button again to clear it.</S.FieldHint>
                </S.FormGroup>
              </S.Col>
              <S.Col>
                {/* 考试批次选择：模板定义了批次时必选 */}
                {availableBatches.length > 0 ? (
                  <S.FormGroup>
                    <S.Label htmlFor="test-response-batch">
                      Exam Batch <span style={{ color: '#dc3545' }}>*</span>
                    </S.Label>
                    <SearchableSelect
                      id="test-response-batch"
                      value={selectedBatchId}
                      onChange={(batchId) => setSelectedBatchId(batchId || '')}
                      clearable={false}
                      placeholder="Select exam batch..."
                      options={availableBatches.map(batch => ({
                        value: batch.id,
                        label: `${batch.name}${batch.id === templateCurrentBatchId ? ' (current)' : ''}`,
                      }))}
                    />
                    <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '4px' }}>
                      Which sitting of this exam the response belongs to. Ranking compares within the batch; predicted scores use all batches.
                    </div>
                  </S.FormGroup>
                ) : (
                  <S.FormGroup>
                    <S.Label>Exam Batch</S.Label>
                    <div style={{ fontSize: '12px', color: '#dc3545', marginTop: '8px' }}>
                      This template has no batches yet. Ask an admin to create one in Test Management (Manage Batches) before entering responses.
                    </div>
                  </S.FormGroup>
                )}
              </S.Col>
            </S.Row>
          )}
            {templateData && (
            <div style={{ marginTop: '15px', padding: '10px', background: '#f8f9fa', borderRadius: '4px' }}>
              <strong>Selected Template:</strong> {templateData.examName} 
              {templateData.examDate && (
                <span> - {new Date(templateData.examDate).toLocaleDateString()}</span>
              )}
            </div>
          )}

          {/* 实时得分显示 */}
          {templateData && (
            (() => {
              const scoreInfo = calculateStudentScore();
              return (
                <div style={{ 
                  marginTop: '15px', 
                  padding: '15px', 
                  background: '#e8f5e8', 
                  borderRadius: '4px',
                  border: '1px solid #28a745'
                }}>
                  <h4 style={{ margin: '0 0 10px 0', color: '#155724' }}>Statistics:</h4>
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                    gap: '10px',
                    marginBottom: '10px'
                  }}>
                    <div style={{ 
                      background: '#d4edda', 
                      padding: '10px', 
                      borderRadius: '4px',
                      textAlign: 'center'
                    }}>
                      <strong>Total</strong><br/>
                      <span style={{ fontSize: '18px', color: '#155724' }}>
                        {scoreInfo.totalScore}/{scoreInfo.maxScore} ({scoreInfo.percentage}%)
                      </span>
                      {scoreInfo.excluded && (scoreInfo.excluded.sections > 0 || scoreInfo.excluded.questions > 0) && (
                        <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '5px' }}>
                          Excluded: {scoreInfo.excluded.sections} sections, {scoreInfo.excluded.questions} questions
                        </div>
                      )}
                    </div>
                    {Object.entries(scoreInfo.sections).map(([sectionName, data]) => (
                      <div key={sectionName} style={{ 
                        background: data.excluded ? '#f8f9fa' : '#d4edda', 
                        padding: '8px', 
                        borderRadius: '4px',
                        textAlign: 'center',
                        fontSize: '14px',
                        border: data.excluded ? '2px dashed #6c757d' : 'none'
                      }}>
                        <strong>{sectionName}</strong><br/>
                        {data.excluded ? 'Excluded' : `${data.current}/${data.max} (${data.percentage}%)`}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()
          )}
        </S.FormSection>        {/* 各个部分 */}
        {templateData && (() => {
          const templateType = templateData.templateType || 'mockTest';
          
          if (templateType === 'finalExam') {
            // 动态根据template中的sections来渲染，而不是使用固定的顺序
            return Object.entries(templateData.sections).map(([sectionType, section]) => {
              if (!section || section.questions.length === 0) return null;
              
              return (
                <div key={sectionType}>
                  {renderFinalExamObjectiveSection(sectionType, section)}
                  {renderFinalExamSubjectiveSection(sectionType, section)}
                </div>
              );
            });          } else {
            // Mock Test 模式：统一按照questionType来处理，和Final Exam保持一致
            return Object.entries(templateData.sections).map(([sectionType, section]) => {
              if (!section || section.questions.length === 0) return null;
              
              return (
                <div key={sectionType}>
                  {renderMockTestObjectiveSection(sectionType, section)}
                  {renderMockTestSubjectiveSection(sectionType, section)}
                </div>
              );
            });
          }
        })()}
      </S.MainContent>
    </S.Container>
  );
};

export default TestResponse;